import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './GamePage.module.css';
import Board from './components/Board/Board';
import HandDisplay from './components/HandDisplay/HandDisplay';
import KifuDisplay from './components/KifuDisplay/KifuDisplay';

// 共有型定義をインポート
import { BoardState, Coordinate, PlayerColor, PieceType, Move, GameStateUpdate, PromotionQueryResponse, GameEndReason } from '../../../../shared/types/game';
import { Socket } from 'socket.io-client';

// ヘルパー関数をインポート
import { createPiece, getOriginalPieceType, getPromotedPieceType } from './utils/pieceUtils';
import { getLegalMoves, getLegalDropMoves } from './utils/shogiRules';
import { formatTime } from './utils/timeUtils';

import { useNavigate } from 'react-router-dom';

interface GamePageProps {
  playerName: string;
  playerId: string;
  roomId: string;
  myColor: PlayerColor;
  opponentPlayerName: string;
  opponentPlayerId: string;
  initialTimeMs: number;
  socket: Socket;
}

// 盤面の初期データ
const initialBoardState: BoardState = [
  [createPiece('kyo', 'gote'), createPiece('kei', 'gote'), createPiece('gin', 'gote'), createPiece('kin', 'gote'), createPiece('ou', 'gote'), createPiece('kin', 'gote'), createPiece('gin', 'gote'), createPiece('kei', 'gote'), createPiece('kyo', 'gote')],
  [null, createPiece('hisha', 'gote'), null, null, null, null, null, createPiece('kaku', 'gote'), null],
  [createPiece('fu', 'gote'), createPiece('fu', 'gote'), createPiece('fu', 'gote'), createPiece('fu', 'gote'), createPiece('fu', 'gote'), createPiece('fu', 'gote'), createPiece('fu', 'gote'), createPiece('fu', 'gote'), createPiece('fu', 'gote')],
  [null, null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null, null],
  [createPiece('fu', 'sente'), createPiece('fu', 'sente'), createPiece('fu', 'sente'), createPiece('fu', 'sente'), createPiece('fu', 'sente'), createPiece('fu', 'sente'), createPiece('fu', 'sente'), createPiece('fu', 'sente'), createPiece('fu', 'sente')],
  [null, createPiece('kaku', 'sente'), null, null, null, null, null, createPiece('hisha', 'sente'), null],
  [createPiece('kyo', 'sente'), createPiece('kei', 'sente'), createPiece('gin', 'sente'), createPiece('kin', 'sente'), createPiece('ou', 'sente'), createPiece('kin', 'sente'), createPiece('gin', 'sente'), createPiece('kei', 'sente'), createPiece('kyo', 'sente')],
];

const GamePage: React.FC<GamePageProps> = ({ playerName, playerId, roomId, myColor, opponentPlayerName, opponentPlayerId, initialTimeMs, socket }) => {
  // リアルタイムゲームの状態
  const [liveBoard, setLiveBoard] = useState<BoardState>(initialBoardState);
  const [liveSenteHand, setLiveSenteHand] = useState<PieceType[]>([]);
  const [liveGoteHand, setLiveGoteHand] = useState<PieceType[]>([]);
  const [liveTurn, setLiveTurn] = useState<PlayerColor>('sente');
  const [liveGameEndReason, setLiveGameEndReason] = useState<GameEndReason>(null);
  const [liveWinner, setLiveWinner] = useState<PlayerColor | null>(null);
  const [liveStalematePlayer, setLiveStalematePlayer] = useState<PlayerColor | null>(null);
  const [liveKifu, setLiveKifu] = useState<Move[]>([]);

  // 選択中のマスと持ち駒（ライブゲームと再生モードで共有）
  const [selectedSquare, setSelectedSquare] = useState<Coordinate | null>(null);
  const [selectedHandPiece, setSelectedHandPiece] = useState<PieceType | null>(null);
  
  // 棋譜再生モードの状態
  const [currentPlaybackMoveIndex, setCurrentPlaybackMoveIndex] = useState<number>(0); // 0手目 (初期盤面)

  // 棋譜再生時の表示状態
  const [playbackBoard, setPlaybackBoard] = useState<BoardState>(initialBoardState);
  const [playbackSenteHand, setPlaybackSenteHand] = useState<PieceType[]>([]);
  const [playbackGoteHand, setPlaybackGoteHand] = useState<PieceType[]>([]);
  const [playbackTurn, setPlaybackTurn] = useState<PlayerColor>('sente');

  // カスタムモーダル関連のstate
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  // modalCallbackをuseRefで管理
  const modalCallbackRef = useRef<((result: boolean) => void) | null>(null);

  // 最新のPromotionQueryResponseを保持するためのref
  const latestPromotionQueryRef = useRef<PromotionQueryResponse | null>(null);
  // モーダルが開いているかどうかを追跡するref
  const isModalOpenRef = useRef<boolean>(false);
  // モーダル表示中に受信したGameStateUpdateを一時的に保持するref
  const pendingGameStateUpdateRef = useRef<GameStateUpdate | null>(null);

  // ハイライト表示する合法手座標のリスト
  const [highlightedMoves, setHighlightedMoves] = useState<Coordinate[]>([]);

  // 持ち時間管理
  const [senteTimeAtLastServerUpdate, setSenteTimeAtLastServerUpdate] = useState<number>(initialTimeMs);
  const [goteTimeAtLastServerUpdate, setGoteTimeAtLastServerUpdate] = useState<number>(initialTimeMs);
  const [clientReceivedTimestamp, setClientReceivedTimestamp] = useState<number>(Date.now());

  // 表示用の残り時間
  const [displaySenteTime, setDisplaySenteTime] = useState<number>(initialTimeMs);
  const [displayGoteTime, setDisplayGoteTime] = useState<number>(initialTimeMs);

  const [errorMessage, setErrorMessage] = useState('');

  const navigate = useNavigate();

  // 指定された棋譜のインデックスまでの盤面と持ち駒、手番を再構築する関数
  const reconstructBoardAndHand = useCallback((kifuMoves: Move[], targetIndex: number) => {
    let tempBoard: BoardState = JSON.parse(JSON.stringify(initialBoardState));
    let tempSenteHand: PieceType[] = [];
    let tempGoteHand: PieceType[] = [];
    let tempTurn: PlayerColor = 'sente';

    for (let i = 0; i < targetIndex; i++) {
      const move = kifuMoves[i];
      if (!move) continue;

      if ('drop' in move && move.drop) { // 駒打ちの場合
        tempBoard[move.to.row][move.to.col] = createPiece(move.drop, tempTurn, false);
        if (tempTurn === 'sente') {
          const idx = tempSenteHand.indexOf(move.drop);
          if (idx > -1) tempSenteHand.splice(idx, 1);
        } else {
          const idx = tempGoteHand.indexOf(move.drop);
          if (idx > -1) tempGoteHand.splice(idx, 1);
        }
      } else if ('from' in move && move.from) { // 駒の移動の場合
        const pieceToMove = tempBoard[move.from.row][move.from.col];
        if (!pieceToMove) {
          console.warn(`[Playback] ${i + 1}手目: 駒が存在しないマスからの移動をスキップします:`, move.from);
          tempTurn = tempTurn === 'sente' ? 'gote' : 'sente'; // 手番だけ進める
          continue;
        }

        const targetPiece = tempBoard[move.to.row][move.to.col];
        if (targetPiece && targetPiece.owner !== tempTurn) {
          // 駒取りの場合
          const takenPieceType = getOriginalPieceType(targetPiece.type, targetPiece.isPromoted);
          if (tempTurn === 'sente') {
            tempSenteHand.push(takenPieceType);
          } else {
            tempGoteHand.push(takenPieceType);
          }
        }

        // 成りの判定
        let newPieceType = pieceToMove.type;
        let newIsPromoted = pieceToMove.isPromoted;
        if (move.promote) {
          newPieceType = getPromotedPieceType(pieceToMove.type);
          newIsPromoted = true;
        }

        tempBoard[move.to.row][move.to.col] = createPiece(newPieceType, tempTurn, newIsPromoted);
        tempBoard[move.from.row][move.from.col] = null;
      }
      tempTurn = tempTurn === 'sente' ? 'gote' : 'sente'; // 手番を交代
    }
    return { board: tempBoard, senteHand: tempSenteHand, goteHand: tempGoteHand, turn: tempTurn };
  }, []);

  // currentPlaybackMoveIndex が変更されたら盤面を更新
  useEffect(() => {
    // ゲームが終了している場合のみ棋譜再生の盤面を更新
    if (liveGameEndReason !== null) {
      console.log(`フロントエンド: 棋譜再生モード: ${currentPlaybackMoveIndex}手目まで盤面を再構築します。`);
      const { board, senteHand, goteHand, turn } = reconstructBoardAndHand(liveKifu, currentPlaybackMoveIndex);
      setPlaybackBoard(board);
      setPlaybackSenteHand(senteHand);
      setPlaybackGoteHand(goteHand);
      setPlaybackTurn(turn);
      console.log('フロントエンド: 棋譜再生盤面更新完了。');
    }
  }, [currentPlaybackMoveIndex, liveKifu, liveGameEndReason, reconstructBoardAndHand]);

  // 保留中のゲーム状態を適用する関数
  const applyPendingGameState = useCallback(() => {
    if (pendingGameStateUpdateRef.current) {
      const newState = pendingGameStateUpdateRef.current;
      console.log('フロントエンド: 保留中のゲーム状態を適用します:', newState);
      setLiveBoard(newState.board);
      setLiveSenteHand(newState.senteHand);
      setLiveGoteHand(newState.goteHand);
      setLiveTurn(newState.turn);
      setLiveKifu(newState.kifu);
      setLiveGameEndReason(newState.gameEndReason);
      setLiveWinner(newState.winner);
      setLiveStalematePlayer(newState.stalematePlayer);
      setSelectedSquare(null);
      setSelectedHandPiece(null);
      setHighlightedMoves([]);

      // サーバーから受け取った残り時間を保存し、受信時刻を記録
      setSenteTimeAtLastServerUpdate(newState.remainingTimes.sente);
      setGoteTimeAtLastServerUpdate(newState.remainingTimes.gote);
      setClientReceivedTimestamp(Date.now()); // 状態を受信した時刻を記録

      if (newState.gameEndReason !== null) {
        setCurrentPlaybackMoveIndex(newState.kifu.length);
        console.log('フロントエンド: ゲーム終了。playback indexを最終手に設定:', newState.kifu.length);
      } else {
        setCurrentPlaybackMoveIndex(newState.kifu.length);
        console.log('フロントエンド: ゲーム進行中。playback indexを最新の棋譜の長さに設定:', newState.kifu.length);
      }
      pendingGameStateUpdateRef.current = null; // 適用後クリア
    }
  }, []);

  useEffect(() => {
    // GamePageに固有のイベントリスナーを登録
    const handleGameState = (newState: GameStateUpdate) => {
      console.log('フロントエンド: 新しいゲーム状態を受信しました:', newState);
      console.log('フロントエンド: 受信した盤面:', newState.board);
      console.log('フロントエンド: 受信した手番:', newState.turn);
      console.log('フロントエンド: 自分の色 (myColor):', myColor);

      if (isModalOpenRef.current) {
        console.log('フロントエンド: モーダルが開いているため、ゲーム状態の更新を保留します。');
        pendingGameStateUpdateRef.current = newState;
      } else {
        setLiveBoard(newState.board);
        setLiveSenteHand(newState.senteHand);
        setLiveGoteHand(newState.goteHand);
        setLiveTurn(newState.turn);
        setLiveKifu(newState.kifu);
        setLiveGameEndReason(newState.gameEndReason);
        setLiveWinner(newState.winner);
        setLiveStalematePlayer(newState.stalematePlayer);
        setSelectedSquare(null);
        setSelectedHandPiece(null);
        setHighlightedMoves([]);

        setSenteTimeAtLastServerUpdate(newState.remainingTimes.sente);
        setGoteTimeAtLastServerUpdate(newState.remainingTimes.gote);
        setClientReceivedTimestamp(Date.now());

        console.log('フロントエンド: liveBoard state updated to:', newState.board);
        console.log('フロントエンド: liveTurn state updated to:', newState.turn);
        console.log('フロントエンド: 現在のmyColor:', myColor, '現在のliveTurn:', newState.turn);

        if (newState.gameEndReason !== null) {
          localStorage.removeItem('shogiRoomId');
          localStorage.removeItem('shogiMyColor');
          localStorage.removeItem('shogiOpponentPlayerId');
          localStorage.removeItem('shogiOpponentPlayerName');
          localStorage.removeItem('shogiInitialTimeMs');
          console.log(`フロントエンド: 対局が終了したため、localStorageからゲーム情報を削除しました。理由: ${newState.gameEndReason}`);
        } else {
          localStorage.setItem('shogiRoomId', roomId); // 一貫性のためにキー名を修正
          console.log(`フロントエンド: 対局中のため、roomId '${roomId}' をlocalStorageに保存しました。`);
        }

        setCurrentPlaybackMoveIndex(newState.kifu.length);
        console.log('フロントエンド: ゲーム終了状態:', newState.gameEndReason, 'playback indexを最新の棋譜の長さに設定:', newState.kifu.length);
      }
    };

    const handleGameError = (error: { message: string }) => {
      console.error('フロントエンド: サーバーからエラーを受信:', error.message);
      setModalMessage(`エラー: ${error.message}`);
      setErrorMessage(error.message);

      const isRoomNotFoundError = error.message === '指定されたルームが見つかりません。';
      const isPlayerIdMissingError = error.message.includes('プレイヤーIDがありません');

      modalCallbackRef.current = () => {
        if (isRoomNotFoundError) {
          console.log('フロントエンド: ルームが見つからないエラーのため、localStorageをクリアし、マッチング画面へ遷移します。');
            localStorage.removeItem('shogiRoomId');
            localStorage.removeItem('shogiMyColor');
            localStorage.removeItem('shogiOpponentPlayerId');
            localStorage.removeItem('shogiOpponentPlayerName');
            localStorage.removeItem('shogiInitialTimeMs');
            navigate('/matching');
        } else if (isPlayerIdMissingError) {
            console.log('フロントエンド: プレイヤーIDがないエラーのため、game:request_state を再試行します。');
            socket.emit('game:request_state', roomId);
        } else {
            console.log('フロントエンド: その他のエラーのため、最新のゲーム状態をサーバーにリクエストします。');
            socket.emit('game:request_state', roomId);
        }
        setShowModal(false);
        isModalOpenRef.current = false;
        setHighlightedMoves([]);
        setErrorMessage(''); // モーダルを閉じたらエラーメッセージもクリア
      };
      setShowModal(true);
      isModalOpenRef.current = true;
      setSelectedSquare(null);
      setSelectedHandPiece(null);
      setHighlightedMoves([]);
    };

    const handlePromotionQuery = (query: PromotionQueryResponse) => {
        console.log('フロントエンド: game:promotion_query イベントを受信しました:', query);
        console.log('フロントエンド: query.canPromote:', query.canPromote, 'query.mustPromote:', query.mustPromote);

        latestPromotionQueryRef.current = query; // 最新のpromotion queryをrefに保存

        if (query.mustPromote) {
            console.log('フロントエンド: 強制成りなので自動的に成ります。');
            const moveToSend = { ...query.move, promote: true, roomId };
            console.log('フロントエンド: 強制成りリクエストを送信します:', moveToSend);
            socket.emit('move:request', moveToSend); // socket prop を使用
        } else if (query.canPromote) {
            console.log('フロントエンド: 任意成りなのでモーダルを表示します。');
            setModalMessage('成りますか？');
            modalCallbackRef.current = (doPromote: boolean) => {
                console.log('フロントエンド: モーダルコールバックが実行されました。成るか？:', doPromote);
                const currentQuery = latestPromotionQueryRef.current;
                if (socket && currentQuery) {
                    const moveToSend = { ...currentQuery.move, promote: doPromote, roomId };
                    console.log('フロントエンド: 成りの選択を送信します:', moveToSend);
                    socket.emit('move:request', moveToSend);
                } else {
                    console.error('フロントエンド: モーダルコールバック: socket または latestPromotionQueryRef.current が null です。');
                }
                isModalOpenRef.current = false;
                applyPendingGameState();
                setHighlightedMoves([]);
            };
            console.log('フロントエンド: modalCallbackRef.current が設定されました。');
            setShowModal(true);
            isModalOpenRef.current = true;
            console.log('フロントエンド: setShowModal(true) が呼び出されました。');
            setHighlightedMoves([]);
        } else {
            console.error('フロントエンド: 不正なpromotion_queryを受信しました。', query);
            setSelectedSquare(null);
            setSelectedHandPiece(null);
            setHighlightedMoves([]);
        }
    };

    // 以下のゲーム終了イベントは、alert表示用として残すが、ゲーム状態はgame:stateで更新される
    const handleGameMate = (data: { winner: PlayerColor }) => {
      console.log('フロントエンド: 詰みが発生しました！ 勝者:', data.winner);
      setModalMessage(`${data.winner === 'sente' ? '▲先手' : '△後手'}の勝ちです！詰み！`);
      modalCallbackRef.current = () => { applyPendingGameState(); };
      setShowModal(true);
      isModalOpenRef.current = true;
      setHighlightedMoves([]);
    };

    const handleGameStalemate = (data: { player: PlayerColor }) => {
      console.log('フロントエンド: 手詰まりが発生しました！プレイヤー:', data.player);
      setModalMessage(`${data.player === 'sente' ? '▲先手' : '△後手'}は手詰まりです。ゲーム終了！`);
      modalCallbackRef.current = () => { applyPendingGameState(); };
      setShowModal(true);
      isModalOpenRef.current = true;
      setHighlightedMoves([]);
    };

    const handleGameDraw = (data: { reason: 'sennichite' }) => {
      console.log('フロントエンド: 引き分けが発生しました！理由:', data.reason);
      setModalMessage(`千日手により引き分けです。ゲーム終了！`);
      modalCallbackRef.current = () => { applyPendingGameState(); };
      setShowModal(true);
      isModalOpenRef.current = true;
      setHighlightedMoves([]);
    };

    const handleGameResignation = (data: { resigner: PlayerColor, winner: PlayerColor }) => {
      console.log('フロントエンド: 投了が発生しました！投了者:', data.resigner, '勝者:', data.winner);
      setModalMessage(`${data.resigner === 'sente' ? '▲先手' : '△後手'}が投了しました。\n${data.winner === 'sente' ? '▲先手' : '△後手'}の勝ちです！`);
      modalCallbackRef.current = () => { applyPendingGameState(); };
      setShowModal(true);
      isModalOpenRef.current = true;
      setHighlightedMoves([]);
    };

    // 時間切れイベントのハンドリング
    const handleGameTimeout = (data: { timedOutPlayer: PlayerColor; winner: PlayerColor }) => {
      console.log(`フロントエンド: 時間切れが発生しました！ ${data.timedOutPlayer}が時間切れです。勝者: ${data.winner}`);
      setModalMessage(`${data.timedOutPlayer === 'sente' ? '▲先手' : '△後手'}が時間切れです。\n${data.winner === 'sente' ? '▲先手' : '△後手'}の勝ちです！`);
      modalCallbackRef.current = () => { applyPendingGameState(); };
      setShowModal(true);
      isModalOpenRef.current = true;
      setHighlightedMoves([]);
    };

    // 接続切れイベントのハンドリング
    const handleGameDisconnected = (data: { disconnectedPlayer: PlayerColor; winner: PlayerColor }) => {
      console.log(`フロントエンド: 接続切れが発生しました！ ${data.disconnectedPlayer}が切断しました。勝者: ${data.winner}`);
      setModalMessage(`${data.disconnectedPlayer === 'sente' ? '▲先手' : '△後手'}が接続切れになりました。\n${data.winner === 'sente' ? '▲先手' : '△後手'}の勝ちです！`);
      modalCallbackRef.current = () => { applyPendingGameState(); };
      setShowModal(true);
      isModalOpenRef.current = true;
      setHighlightedMoves([]);
    };

    // イベントリスナーを登録
    socket.emit('room:join', roomId); // コンポーネントマウント時にルーム参加リクエストを送信
    console.log(`フロントエンド: ルーム ${roomId} に参加リクエストを送信します。`);

    socket.on('game:state', handleGameState);
    socket.on('game:error', handleGameError);
    socket.on('game:promotion_query', handlePromotionQuery);
    socket.on('game:mate', handleGameMate);
    socket.on('game:stalemate', handleGameStalemate);
    socket.on('game:draw', handleGameDraw);
    socket.on('game:resignation', handleGameResignation);
    socket.on('game:timeout', handleGameTimeout);
    socket.on('game:disconnected', handleGameDisconnected);

    return () => {
      console.log('フロントエンド: GamePageアンマウント、Socket.IOイベントリスナーを解除します。');
      socket.off('game:state', handleGameState);
      socket.off('game:error', handleGameError);
      socket.off('game:promotion_query', handlePromotionQuery);
      socket.off('game:mate', handleGameMate);
      socket.off('game:stalemate', handleGameStalemate);
      socket.off('game:draw', handleGameDraw);
      socket.off('game:resignation', handleGameResignation);
      socket.off('game:timeout', handleGameTimeout);
      socket.off('game:disconnected', handleGameDisconnected);

      // ルーム退出リクエストは App.tsx の socket.on('disconnect') で処理されるか、
      // handleBackToMatchingScreen で明示的に送信されるため、ここでは不要
      // ただし、明示的にルームを離れる必要がある場合は、ここに socket.emit('room:leave', roomId); を追加
      // 現状は handleBackToMatchingScreen で行われているため、ここでは何もしない
    };
  }, [playerId, playerName, roomId, myColor, applyPendingGameState, initialTimeMs, socket, navigate]);

  // クライアント側でのタイマー表示更新ロジック
  useEffect(() => {
    let timerInterval: NodeJS.Timeout | null = null;

    // isGameEnded は liveGameEndReason !== null で判断
    const isGameActive = liveGameEndReason === null;

    if (isGameActive) {
      timerInterval = setInterval(() => {
        const now = Date.now();
        const elapsedTimeSinceLastServerUpdate = now - clientReceivedTimestamp;

        // 現在の手番のプレイヤーの残り時間を計算して表示
        if (liveTurn === 'sente') {
          setDisplaySenteTime(Math.max(0, senteTimeAtLastServerUpdate - elapsedTimeSinceLastServerUpdate));
          setDisplayGoteTime(goteTimeAtLastServerUpdate); // 相手の時間はそのまま
        } else { // liveTurn === 'gote'
          setDisplayGoteTime(Math.max(0, goteTimeAtLastServerUpdate - elapsedTimeSinceLastServerUpdate));
          setDisplaySenteTime(senteTimeAtLastServerUpdate); // 相手の時間はそのまま
        }
      }, 100); // 100msごとに更新してより滑らかな表示に

    } else {
      // ゲームが非アクティブになったら、最後にサーバーから受け取った時間を表示
      setDisplaySenteTime(senteTimeAtLastServerUpdate);
      setDisplayGoteTime(goteTimeAtLastServerUpdate);
    }

    return () => {
      if (timerInterval) clearInterval(timerInterval);
    };
  }, [liveGameEndReason, liveTurn, senteTimeAtLastServerUpdate, goteTimeAtLastServerUpdate, clientReceivedTimestamp]);

  const isGameEnded = liveGameEndReason !== null;

  // 現在表示すべき盤面と持ち駒、手番を決定
  const currentBoard = isGameEnded ? playbackBoard : liveBoard;
  const currentSenteHand = isGameEnded ? playbackSenteHand : liveSenteHand;
  const currentGoteHand = isGameEnded ? playbackGoteHand : liveGoteHand;
  const currentTurn = isGameEnded ? playbackTurn : liveTurn;
  const currentKifu = liveKifu;

  // 盤面や持ち駒のクリックハンドラ（棋譜再生モードでは無効化）
  const handleSquareClick = (row: number, col: number) => {
    setErrorMessage('');
    console.log(`handleSquareClick: クリックされました。myColor: ${myColor}, currentTurn: ${currentTurn}`);
    if (isGameEnded) { // ゲーム終了時は操作無効
        console.log("フロントエンド: ゲームは終了しています。操作は無効です。");
        setHighlightedMoves([]);
        return;
    }
    // 自分の手番でない場合は操作を許可しない
    if (currentTurn !== myColor) {
        console.log("フロントエンド: 自分の手番ではありません。操作できません。");
        setSelectedSquare(null);
        setSelectedHandPiece(null);
        setHighlightedMoves([]);
        return;
    }
    // ソケット接続チェック
    if (!socket || !socket.connected) {
      setErrorMessage('サーバーに接続していません。しばらく待ってから再度お試しください。');
      return;
    }

    const clickedPieceOnBoard = currentBoard[row][col];

    if (selectedHandPiece !== null) {
      // 持ち駒が選択されている場合 (駒打ち)
      const currentHand = myColor === 'sente' ? currentSenteHand : currentGoteHand;
      if (!currentHand.includes(selectedHandPiece)) {
        console.log("フロントエンド: 持っていない駒を打とうとしています。");
        setSelectedSquare(null);
        setSelectedHandPiece(null);
        setHighlightedMoves([]);
        return;
      }

      console.log('handleSquareClick: 持ち駒が選択されています。駒打ちを試みます。');
      if (clickedPieceOnBoard === null) {
        const move: Move = {
          drop: selectedHandPiece,
          to: { row, col }
        };
        console.log(`フロントエンド: ${myColor}が駒打ちリクエストを送信します:`, move, 'to room:', roomId);
        socket.emit('move:request', { ...move, roomId });
        setSelectedHandPiece(null);
        setHighlightedMoves([]);
      } else {
        console.log("フロントエンド: 持ち駒を置くには空マスを選択してください。");
        setSelectedHandPiece(null);
        setHighlightedMoves([]);
      }
      setSelectedSquare(null);
      return;
    }

    if (selectedSquare === null) {
      if (clickedPieceOnBoard !== null && clickedPieceOnBoard.owner === myColor) {
        console.log('handleSquareClick: 盤上の自分の駒を選択しました:', clickedPieceOnBoard.type, 'at', { row, col });
        setSelectedSquare({ row, col });
        // 合法手を計算してハイライト
        const piece = currentBoard[row][col];
        if (piece) {
          const legalMovesForPiece = getLegalMoves(currentBoard, { row, col }, piece, myColor);
          console.log('フロントエンド: 計算された合法手:', legalMovesForPiece);
          setHighlightedMoves(legalMovesForPiece);
        }
      } else {
        console.log("フロントエンド: 自分の駒ではない、または空マスをクリックしました。選択を解除します。");
        setSelectedSquare(null);
        setHighlightedMoves([]);
      }
    } else {
      const { row: prevRow, col: prevCol } = selectedSquare;
      const pieceToMove = currentBoard[prevRow][prevCol];

      if (!pieceToMove) {
        console.log("フロントエンド: 選択されていたマスに駒がありません。選択を解除します。");
        setSelectedSquare(null);
        setHighlightedMoves([]);
        return;
      }

      if (pieceToMove.owner !== myColor) {
        console.log("フロントエンド: 選択されていた駒が自分の駒ではありません。選択を解除します。");
        setSelectedSquare(null);
        setHighlightedMoves([]);
        return;
      }

      if (prevRow === row && prevCol === col) {
        console.log('handleSquareClick: 同じマスを再度クリックしました。選択解除。');
        setSelectedSquare(null);
        setHighlightedMoves([]);
      } else {
        const move: Move = {
          from: { row: prevRow, col: prevCol },
          to: { row, col },
        };

        console.log(`フロントエンド: ${myColor}が指し手リクエストを送信します (成りの確認なし):`, move, 'to room:', roomId);
        socket.emit('move:request', { ...move, roomId });

        setSelectedSquare(null);
        setHighlightedMoves([]);
      }
    }
  };

  const handleHandPieceClick = (pieceType: PieceType) => {
    setErrorMessage('');
    console.log(`handleHandPieceClick: クリックされました。myColor: ${myColor}, currentTurn: ${currentTurn}`);
    if (isGameEnded) { //棋譜再生モード中も操作無効
        console.log("フロントエンド: ゲームは終了しているか、棋譜再生モード中です。操作は無効です。");
        setHighlightedMoves([]);
        return;
    }
    // 自分の手番でない場合は操作を許可しない
    if (currentTurn !== myColor) {
        console.log("フロントエンド: 自分の手番ではありません。持ち駒を選択できません。");
        setSelectedSquare(null);
        setSelectedHandPiece(null);
        setHighlightedMoves([]);
        return;
    }
    // ソケット接続チェック
    if (!socket || !socket.connected) {
      setErrorMessage('サーバーに接続していません。しばらく待ってから再度お試しください。');
      return;
    }

    const currentHand = myColor === 'sente' ? currentGoteHand : currentSenteHand;
    const canSelectHandPiece = currentHand.includes(pieceType);

    if (!canSelectHandPiece) {
      console.log("フロントエンド: 持っていない駒をクリックしました。");
      setSelectedSquare(null);
      setSelectedHandPiece(null);
      setHighlightedMoves([]);
      return;
    }

    if (selectedSquare !== null) {
      console.log('handleHandPieceClick: 盤上の駒が選択されていたため、選択を解除し、持ち駒を選択します。');
      setSelectedSquare(null);
      setSelectedHandPiece(pieceType);
      setHighlightedMoves([]);
    } else if (selectedHandPiece === pieceType) {
      console.log('handleHandPieceClick: 同じ持ち駒を再度クリックしました。選択解除。');
      setSelectedHandPiece(null);
      setHighlightedMoves([]);
    } else {
      console.log('handleHandPieceClick: 持ち駒を選択しました:', pieceType);
      setSelectedHandPiece(pieceType);
      // 持ち駒を選択した場合、駒打ちできるマスをハイライト
      const dropLegalMoves = getLegalDropMoves(currentBoard, pieceType, myColor);
      console.log('フロントエンド: 計算された駒打ちの合法手:', dropLegalMoves);
      setHighlightedMoves(dropLegalMoves);
    }
  };

  const handleResignClick = () => {
    setErrorMessage('');
    console.log('handleResignClick: 投了ボタンがクリックされました。');
    if (isGameEnded) { // ゲーム終了時は操作無効
        setModalMessage("ゲームは既に終了しています。");
        modalCallbackRef.current = () => { applyPendingGameState(); };
        setShowModal(true);
        isModalOpenRef.current = true;
        console.log('handleResignClick: ゲーム終了のため、モーダル表示を試みます。showModal:', true);
        setHighlightedMoves([]);
        return;
    }
    if (currentTurn !== myColor) {
        setModalMessage("自分の手番ではありません。投了できません。");
        modalCallbackRef.current = () => { applyPendingGameState(); };
        setShowModal(true);
        isModalOpenRef.current = true;
        console.log('handleResignClick: 自分の手番ではないため、モーダル表示を試みます。showModal:', true);
        setHighlightedMoves([]);
        return;
    }
    // ソケット接続チェック
    if (!socket || !socket.connected) {
      setErrorMessage('サーバーに接続していません。しばらく待ってから再度お試しください。');
      return;
    }

    // カスタムモーダルを表示してユーザーに確認
    setModalMessage("本当に投了しますか？");
    modalCallbackRef.current = (confirmResign: boolean) => {
        if (confirmResign) {
            const resignationData = { player: myColor, roomId };
            console.log(`フロントエンド: ${myColor} が投了をリクエストします:`, resignationData);
            socket.emit('resignation:request', resignationData);
        }
        isModalOpenRef.current = false; // モーダルが閉じることをマーク
        applyPendingGameState(); // 保留中のゲーム状態を適用
        setHighlightedMoves([]);
    };
    setShowModal(true);
    isModalOpenRef.current = true;
    console.log('handleResignClick: 投了確認モーダル表示を試みます。showModal:', true);
    setHighlightedMoves([]);
  };

  // 棋譜再生コントロールハンドラ
  const handlePlaybackControl = (action: 'first' | 'prev' | 'next' | 'last') => {
    // ゲームが終了している場合のみ操作を許可
    if (!isGameEnded) {
      console.log("ゲームが終了していないため、棋譜再生はできません。");
      return;
    }

    switch (action) {
      case 'first':
        setCurrentPlaybackMoveIndex(0); // 0手目 (初期盤面)
        console.log('フロントエンド: 棋譜再生: 最初の局面へ移動。');
        break;
      case 'prev':
        setCurrentPlaybackMoveIndex(prev => Math.max(0, prev - 1));
        console.log('フロントエンド: 棋譜再生: 1手戻る。');
        break;
      case 'next':
        setCurrentPlaybackMoveIndex(prev => Math.min(liveKifu.length, prev + 1));
        console.log('フロントエンド: 棋譜再生: 1手進む。');
        break;
      case 'last':
        setCurrentPlaybackMoveIndex(liveKifu.length);
        console.log('フロントエンド: 棋譜再生: 最終局面へ移動。');
        break;
      default:
        break;
    }
    setSelectedSquare(null);
    setSelectedHandPiece(null);
    setHighlightedMoves([]);
  };

  // マッチング画面に戻るボタンのハンドラ
  const handleBackToMatchingScreen = () => {
    console.log('「マッチング画面に戻る」ボタンがクリックされました。localStorageをクリアします。');
    if (socket && roomId) {
      console.log(`Socket.emit('room:leave', ${roomId}) を送信します。`);
      socket.emit('room:leave', roomId);
    } else {
      console.warn('SocketまたはroomIdが利用できないため、room:leaveイベントを送信できません。');
    }
    // localStorageからゲームセッション情報を削除
    localStorage.removeItem('shogiRoomId');
    localStorage.removeItem('shogiMyColor');
    localStorage.removeItem('shogiOpponentPlayerId');
    localStorage.removeItem('shogiOpponentPlayerName');
    localStorage.removeItem('shogiInitialTimeMs');
    // モーダルを閉じ、App.tsxがlocalStorageの変更を検知して画面遷移することを期待
    setShowModal(false); 
    isModalOpenRef.current = false;
    navigate('/matching'); 
  };

  // カスタムモーダルのレンダリング
  const renderModal = () => {
    if (!showModal) return null;

    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <p>{modalMessage}</p>
          <div className={styles.modalActions}>
            <button
              className={styles.modalButtonYes}
              onClick={() => {
                console.log('Modal Yes button clicked. Attempting to call modalCallbackRef.current with true.');
                if (modalCallbackRef.current) {
                  modalCallbackRef.current(true);
                } else {
                  console.warn('Modal Yes button clicked, but modalCallbackRef.current is null or undefined.');
                }
                setShowModal(false);
                isModalOpenRef.current = false;
                modalCallbackRef.current = null;
                applyPendingGameState(); // モーダルを閉じた後、保留中のゲーム状態を適用
                setHighlightedMoves([]);
              }}
            >
              はい
            </button>
            {(modalMessage === '成りますか？' || modalMessage === '本当に投了しますか？') && (
              <button
                className={styles.modalButtonNo}
                onClick={() => {
                  console.log('Modal No button clicked. Attempting to call modalCallbackRef.current with false.');
                  if (modalCallbackRef.current) {
                    modalCallbackRef.current(false);
                  } else {
                    console.warn('Modal No button clicked, but modalCallbackRef.current is null or undefined.');
                  }
                  setShowModal(false);
                  isModalOpenRef.current = false;
                  modalCallbackRef.current = null;
                  applyPendingGameState(); // モーダルを閉じた後、保留中のゲーム状態を適用
                  setHighlightedMoves([]);
                }}
              >
                いいえ
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.gamePageContainer}>
      {renderModal()}
      {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>} {/* エラーメッセージ表示 */}

      <div className={styles.mainGameArea}>
        <div className={styles.shogiElementsWrapper}>
          <div className={styles.shogiElementsHeader}>
            <div className={styles.turnDisplay}>
              手番: {currentTurn === 'sente' ? '▲先手' : '△後手'} ({currentTurn === myColor ? 'あなたの番' : '相手の番'})
              {isGameEnded && currentPlaybackMoveIndex < liveKifu.length && ` (再生中: ${currentPlaybackMoveIndex}手目)`}
            </div>
            <button
              onClick={handleResignClick}
              disabled={isGameEnded || currentTurn !== myColor}
              className={styles.resignButton}
            >
              投了
            </button>
          </div>

          {/* 持ち駒の表示順序をmyColorに応じて変更 */}
          {myColor === 'sente' ? (
            <>
              {/* 後手側 (相手) の情報と持ち駒とタイマー */}
              <div className={styles.playerInfoAndHandTimerWrapper}> {/* 新しいラッパー */}
                <p className={styles.playerNameDisplay}>{opponentPlayerName}</p> {/* 相手の名前 */}
                <div className={styles.handTimerContent}> {/* 持ち駒とタイマーのコンテンツ */}
                  <div className={`${styles.timer} ${liveTurn === 'gote' ? styles.activeTimer : ''}`}>
                    {formatTime(displayGoteTime)}
                  </div>
                  <HandDisplay
                    pieces={currentGoteHand}
                    owner="gote"
                    viewingPlayerColor={myColor}
                    onPieceClick={handleHandPieceClick}
                    selectedPiece={selectedHandPiece}
                    className={styles.handDisplayRoot}
                  />
                </div>
              </div>
              <Board
                boardData={currentBoard}
                selectedSquare={selectedSquare}
                onSquareClick={handleSquareClick}
                currentPlayer={myColor}
                highlightedMoves={highlightedMoves}
              />
              {/* 先手側 (自分) の情報と持ち駒とタイマー */}
              <div className={styles.playerInfoAndHandTimerWrapper}> {/* 新しいラッパー */}
                <div className={styles.handTimerContent}> {/* 持ち駒とタイマーのコンテンツ */}
                  <HandDisplay
                    pieces={currentSenteHand}
                    owner="sente"
                    viewingPlayerColor={myColor}
                    onPieceClick={handleHandPieceClick}
                    selectedPiece={selectedHandPiece}
                    className={styles.handDisplayRoot}
                  />
                  <div className={`${styles.timer} ${liveTurn === 'sente' ? styles.activeTimer : ''}`}>
                    {formatTime(displaySenteTime)}
                  </div>
                </div>
                <p className={styles.playerNameDisplay}>{playerName}</p> {/* 自分の名前 */}
              </div>
            </>
          ) : (
            <>
              {/* 先手側 (相手) の情報と持ち駒とタイマー */}
              <div className={styles.playerInfoAndHandTimerWrapper}> {/* 新しいラッパー */}
                <p className={styles.playerNameDisplay}>{opponentPlayerName}</p> {/* 相手の名前 */}
                <div className={styles.handTimerContent}> {/* 持ち駒とタイマーのコンテンツ */}
                  <div className={`${styles.timer} ${liveTurn === 'sente' ? styles.activeTimer : ''}`}>
                    {formatTime(displaySenteTime)}
                  </div>
                  <HandDisplay
                    pieces={currentSenteHand}
                    owner="sente"
                    viewingPlayerColor={myColor}
                    onPieceClick={handleHandPieceClick}
                    selectedPiece={selectedHandPiece}
                    className={styles.handDisplayRoot}
                  />
                </div>
              </div>
              <Board
                boardData={currentBoard}
                selectedSquare={selectedSquare}
                onSquareClick={handleSquareClick}
                currentPlayer={myColor}
                highlightedMoves={highlightedMoves}
              />
              {/* 後手側 (自分) の情報と持ち駒とタイマー */}
              <div className={styles.playerInfoAndHandTimerWrapper}> {/* 新しいラッパー */}
                <div className={styles.handTimerContent}> {/* 持ち駒とタイマーのコンテンツ */}
                  <HandDisplay
                    pieces={currentGoteHand}
                    owner="gote"
                    viewingPlayerColor={myColor}
                    onPieceClick={handleHandPieceClick}
                    selectedPiece={selectedHandPiece}
                    className={styles.handDisplayRoot}
                  />
                  <div className={`${styles.timer} ${liveTurn === 'gote' ? styles.activeTimer : ''}`}>
                    {formatTime(displayGoteTime)}
                  </div>
                </div>
                <p className={styles.playerNameDisplay}>{playerName}</p> {/* 自分の名前 */}
              </div>
            </>
          )}
        </div>

        <div className={styles.kifuPlaybackWrapper}>
          {isGameEnded && (
            <div className={styles.kifuControls}>
              <button
                onClick={() => handlePlaybackControl('first')}
                disabled={currentPlaybackMoveIndex === 0}
                className={`${styles.playbackButton} ${styles.green}`}
              >
                ＜＜
              </button>
              <button
                onClick={() => handlePlaybackControl('prev')}
                disabled={currentPlaybackMoveIndex === 0}
                className={`${styles.playbackButton} ${styles.blue}`}
              >
                ＜
              </button>
              <button
                onClick={() => handlePlaybackControl('next')}
                disabled={currentPlaybackMoveIndex === liveKifu.length}
                className={`${styles.playbackButton} ${styles.blue}`}
              >
                ＞
              </button>
              <button
                onClick={() => handlePlaybackControl('last')}
                disabled={currentPlaybackMoveIndex === liveKifu.length}
                className={`${styles.playbackButton} ${styles.green}`}
              >
                ＞＞
              </button>
            </div>
          )}
          
          <KifuDisplay
            kifu={currentKifu}
            board={currentBoard}
            senteHand={currentSenteHand}
            goteHand={currentGoteHand}
            gameEndReason={liveGameEndReason}
            winner={liveWinner}
            stalematePlayer={liveStalematePlayer}
            highlightedMoveIndex={currentPlaybackMoveIndex > 0 ? currentPlaybackMoveIndex - 1 : null}
          />
        </div>

        {/* 「マッチング画面に戻る」ボタン */}
        {isGameEnded && (
          <button
            onClick={handleBackToMatchingScreen}
            className={styles.backToMatchingButton}
          >
            マッチング画面に戻る
          </button>
        )}
      </div>
    </div>
  );
};

export default GamePage;